import axios from "axios";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/errors";

export { formatApiErrorDetail };

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Access token is held in memory only (NOT localStorage) to remove the XSS
// token-theft surface. Authentication persists across reloads via the httpOnly
// access_token/refresh_token cookies (sent automatically with withCredentials).
let accessToken = null;
export const getToken = () => accessToken;
export const setToken = (t) => { accessToken = t; };
export const clearToken = () => { accessToken = null; };

export const api = axios.create({
  baseURL: API,
  timeout: 180000, // AI generation can take up to ~2 min
  withCredentials: true, // send httpOnly auth cookies
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const AUTH_SKIP = [
  "/auth/login", "/auth/refresh", "/auth/forgot-password", "/auth/reset-password",
  // MFA step-up endpoints legitimately return 401 on a wrong code/password —
  // never treat those as session expiry.
  "/auth/mfa/verify", "/auth/mfa/enroll/begin", "/auth/mfa/enroll/confirm", "/auth/mfa/disable",
  "/auth/mfa/recovery/regenerate",
];
const isAuthSkip = (url = "") => AUTH_SKIP.some((p) => url.includes(p));

const PUBLIC_PATHS = ["/login", "/portal/login", "/accept-invite", "/portal-invite", "/forgot-password", "/reset-password", "/scope", "/posture"];
const redirectToLogin = () => {
  clearToken();
  const path = window.location.pathname || "";
  if (!PUBLIC_PATHS.some((p) => path.startsWith(p))) window.location.href = "/login";
};

// Single-flight refresh: queue concurrent 401s behind one refresh call.
let refreshing = null;

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    const status = err.response?.status;

    if (status === 401 && original && !original._retry && !isAuthSkip(original.url)) {
      original._retry = true;
      try {
        refreshing =
          refreshing ||
          axios.post(`${API}/auth/refresh`, {}, { withCredentials: true }).finally(() => {
            refreshing = null;
          });
        const { data } = await refreshing;
        if (data?.access_token) setToken(data.access_token);
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${data.access_token}`;
        return api(original);
      } catch (e) {
        redirectToLogin();
        return Promise.reject(e);
      }
    }

    if (status === 401 && !isAuthSkip(original?.url)) redirectToLogin();

    // Global safety net: surface unexpected server/network errors that pages
    // rarely handle themselves. Form-level 4xx are left to the caller.
    if (!original?._silent) {
      if (!err.response) {
        toast.error("Network error — please check your connection and try again.");
      } else if (status >= 500) {
        const rid = err.response.data?.request_id || err.response.headers?.["x-request-id"];
        toast.error(`Server error${rid ? ` · ref ${rid}` : ""}. Please try again or contact support.`);
      }
    }
    return Promise.reject(err);
  }
);
